import { useEffect, useRef, useState, useCallback } from "react";
import { Client } from "@stomp/stompjs";
import apiService from "../services/apiService";
import logger from "../utils/logger";
import * as appStorage from "../utils/appIndexedDB";

/**
 * 將 http(s) 後端／BFF 基底網址轉成 STOMP 用的 ws(s) URL（純 WebSocket，不使用 SockJS）。
 * 若已是 ws(s):// 則原樣回傳。
 *
 * 一併處理常見會觸發 ERR_UNKNOWN_URL_SCHEME 的寫法：
 * - 協定相對網址 //host/path（瀏覽器不會當成 ws）
 * - 僅路徑 /bffws/web/ws（補成與目前頁面同源的 http(s) 再轉 ws）
 */
export function toStompWebSocketUrl(url) {
  if (!url || typeof url !== "string") {
    return url;
  }
  let trimmed = url.trim().replace(/\/$/, "");
  if (/^wss?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  // 協定相對：//example.com/bffws/web/ws
  if (/^\/\//.test(trimmed) && typeof window !== "undefined" && window.location) {
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    trimmed = proto + trimmed;
    return trimmed;
  }
  // 僅路徑：/bffws/web/ws → 依目前頁面 origin 補完整 URL
  if (trimmed.startsWith("/") && typeof window !== "undefined" && window.location) {
    try {
      trimmed = new URL(trimmed, window.location.origin).href;
    } catch {
      /* fall through */
    }
  }
  if (/^https:\/\//i.test(trimmed)) {
    return trimmed.replace(/^https:\/\//i, "wss://");
  }
  if (/^http:\/\//i.test(trimmed)) {
    return trimmed.replace(/^http:\/\//i, "ws://");
  }
  return trimmed;
}

/** 若格式不合法，new WebSocket 可能出現 ERR_UNKNOWN_URL_SCHEME */
function isValidWsUrl(wsUrl) {
  return typeof wsUrl === "string" && /^wss?:\/\/.+/i.test(wsUrl);
}

const TOKEN_STORAGE_KEY = 'mygenie_token';
const REFRESH_TOKEN_STORAGE_KEY = 'mygenie_refresh_token';

/**
 * 驗證並刷新 token（如果需要）
 *
 * @returns {Promise<{success: boolean|null, token: string|null}>}
 *   success === true  → token 有效（或剛刷新成功），可以連線
 *   success === false → 伺服器明確判定 token/refresh token 無效，該導回登入頁
 *   success === null  → 無法確認（verify/refresh 連伺服器都連不上，例如手機網路瞬斷），
 *                        不能當作 token 無效，呼叫端應跳過本次連線、交給下一次自動重連再試
 */
export async function ensureValidToken(currentToken) {
  const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

  if (!currentToken) {
    logger.warn("WebSocket", "沒有 token，無法建立連線");
    return { success: false, token: null };
  }

  // 先驗證 token 是否過期（verifyToken 只回報有效性，refresh 統一由下面這裡處理）。
  // verifyToken() 內部已自行 catch，正常不會拋出例外；這裡的 try/catch 純粹防呆，
  // 任何非預期例外一律視為「無法確認」，不可直接當成 token 無效。
  let verifyResult;
  try {
    verifyResult = await apiService.verifyToken(currentToken);
  } catch (error) {
    logger.warn("WebSocket", "Token 驗證發生非預期例外", { error: error.message });
    verifyResult = { success: false, networkError: true };
  }

  if (verifyResult.success) {
    return { success: true, token: currentToken };
  }

  // 沒收到伺服器回應（離線/逾時/連線中斷）：不能當作「token 無效」，
  // 否則手機網路瞬斷會被誤判成憑證過期而強制登出。維持現有 token，交由呼叫端
  // 跳過本次連線嘗試，等 STOMP 下一次自動重連（reconnectDelay）再驗證一次。
  if (verifyResult.networkError) {
    logger.warn("WebSocket", "Token 驗證時連不上伺服器，暫不判定為失效，等待下次重連");
    return { success: null, token: currentToken };
  }

  // 到這裡代表伺服器明確回覆 token 無效，才進入 refresh 流程
  if (!refreshToken) {
    logger.error("WebSocket", "Token 已過期且無 refresh token，無法建立連線");
    return { success: false, token: null };
  }

  const refreshResult = await apiService.refreshToken(refreshToken);

  if (refreshResult.success && refreshResult.token) {
    logger.success("WebSocket", "Token 已刷新");
    return { success: true, token: refreshResult.token };
  }

  if (refreshResult.networkError) {
    logger.warn("WebSocket", "Refresh Token 時連不上伺服器，暫不判定為失效，等待下次重連");
    return { success: null, token: currentToken };
  }

  logger.error("WebSocket", "RefreshToken 錯誤，無法建立連線", {
    message: refreshResult.message
  });
  return { success: false, token: null };
}

/**
 * 建立 STOMP 連線並回傳 client & 狀態
 * 支援 token 自動驗證與刷新
 *
 * @param {string} endpoint WebSocket 基底 URL（http(s) 會自動轉成 ws(s)），例如 "https://host/bffws/web/ws"
 * @param {object} connectHeaders 連線 headers（包含 Authorization）
 */
export function useStompClient(endpoint, connectHeaders = {}) {
  const clientRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const connectHeadersRef = useRef(connectHeaders);

  // 更新 connectHeaders 的引用
  useEffect(() => {
    connectHeadersRef.current = connectHeaders;
  }, [connectHeaders]);

  // 驗證 token 並建立連線的函數
  const connectWithTokenValidation = useCallback(async () => {
    if (!endpoint) {
      return;
    }

    // 從 connectHeaders 中提取 token
    const authHeader = connectHeadersRef.current?.Authorization || '';
    const currentToken = authHeader.replace(/^Bearer\s+/i, '');

    if (!currentToken) {
      logger.warn("WebSocket", "連線 headers 中沒有 token");
      return;
    }

    // 驗證並刷新 token（如果需要）
    const tokenResult = await ensureValidToken(currentToken);

    if (tokenResult.success === null) {
      // 無法確認（連不上伺服器），不可視為 token 無效：安靜跳過本次連線，
      // 不 deactivate、不彈窗，讓使用者原本的連線狀態不受單純網路瞬斷影響。
      logger.warn("WebSocket", "無法確認 token 是否有效（連不上伺服器），暫緩本次連線");
      return;
    }

    if (!tokenResult.success || !tokenResult.token) {
      logger.error("WebSocket", "無法取得有效的 token，跳過連線，導回登入頁");
      setConnected(false);
      if (clientRef.current) {
        await clientRef.current.deactivate();
      }
      apiService.showReauthRequiredDialog(() => apiService.clearAuthAndRedirectToLogin());
      return;
    }

    // 如果 token 已更新，更新 connectHeaders
    const updatedHeaders = {
      ...connectHeadersRef.current,
      Authorization: `Bearer ${tokenResult.token}`,
    };

    // 如果 client 已存在，先停用
    if (clientRef.current) {
      clientRef.current.deactivate();
    }

    const wsUrl = toStompWebSocketUrl(endpoint);
    if (!isValidWsUrl(wsUrl)) {
      logger.error("WebSocket", "WebSocket 網址不合法（需 ws:// 或 wss://）請檢查 REACT_APP_WS_ENDPOINT", {
        endpoint,
        resolved: wsUrl,
      });
      setConnected(false);
      return;
    }

    // 建立新的 STOMP Client（純 WebSocket；憑證在 STOMP CONNECT frame 的 Authorization）
    const client = new Client({
      webSocketFactory: () => new WebSocket(wsUrl),
      connectHeaders: updatedHeaders,

      // 自動重連 (毫秒)
      reconnectDelay: 5000,

      // 心跳設定（30 秒，與後端一致）
      heartbeatIncoming: 30000,
      heartbeatOutgoing: 30000,

      debug: (str) => {
        console.log("[STOMP]", str);
      },
    });

    client.onConnect = () => {
      logger.success("WebSocket", "STOMP 連線成功");
      console.log("✅ STOMP connected");
      setConnected(true);
      // // 連線成功後，標記目前正在使用 WebSocket
      // try {
      //   if (typeof window !== 'undefined' && window.localStorage) {
      //     window.localStorage.setItem('nowIsWebSocket', 'true');
      //   }
      // } catch (error) {
      //   logger.warn("WebSocket", "設定 nowIsWebSocket 為 true 時失敗，將忽略此錯誤", {
      //     error: error.message,
      //   });
      // }
    };

    client.onStompError = (frame) => {
      const errorMessage = frame.headers["message"] || "未知錯誤";
      logger.error("WebSocket", "STOMP 錯誤", {
        message: errorMessage,
        body: frame.body
      });
      console.error("❌ STOMP error", errorMessage, frame.body);
      
      // 如果是認證錯誤（401/403），可能需要刷新 token
      if (errorMessage.includes('401') || errorMessage.includes('403') || 
          errorMessage.includes('認證') || errorMessage.includes('未授權')) {
        logger.warn("WebSocket", "檢測到認證錯誤，將在下次重連時刷新 token");
        // 觸發重新驗證 token（通過更新 endpoint 來觸發重新連線）
        setConnected(false);
      }
    };

    client.onWebSocketClose = () => {
      logger.info("WebSocket", "STOMP WebSocket 連線關閉");
      console.log("🔌 STOMP WebSocket closed");
      setConnected(false);
      // 連線關閉時，標記目前不再使用 WebSocket
      // try {
      //   if (typeof window !== 'undefined' && window.localStorage) {
      //     window.localStorage.setItem('nowIsWebSocket', 'false');
      //   }
      // } catch (error) {
      //   logger.warn("WebSocket", "設定 nowIsWebSocket 為 false 時失敗，將忽略此錯誤", {
      //     error: error.message,
      //   });
      // }
    };

    // 在重連前驗證 token
    const originalBeforeConnect = client.beforeConnect;
    client.beforeConnect = async () => {
      logger.info("WebSocket", "準備重新連線，驗證 token...");
      
      // 從 localStorage 取得最新的 token（確保使用最新值）
      const latestToken = await appStorage.getItem(TOKEN_STORAGE_KEY);
      const refreshToken = await appStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      
      if (!latestToken) {
        logger.error("WebSocket", "重連時找不到 token，取消重連並導回登入頁");
        setConnected(false);
        // beforeConnect 的回傳值不會被 stompjs 檢查，光 return false 並不會真的停止自動重連，
        // 必須主動呼叫 deactivate() 才能讓 client.active 變 false、停止排程下一次重連
        await client.deactivate();
        apiService.showReauthRequiredDialog(() => apiService.clearAuthAndRedirectToLogin());
        return false;
      }

      // 驗證並刷新 token（如果需要）
      const tokenResult = await ensureValidToken(latestToken);

      if (tokenResult.success === null) {
        // 無法確認（連不上伺服器，例如手機網路瞬斷）：不可視為 token 無效。
        // 不 deactivate、不彈窗，沿用現有 token 讓這次連線照常嘗試——
        // 若 token 真的失效，連線會在 WebSocket 層被拒絕，下次 beforeConnect 會再驗證一次；
        // 若只是網路瞬斷，網路恢復後這次或下次重連就會成功。
        logger.warn("WebSocket", "重連時無法確認 token 是否有效（連不上伺服器），沿用現有 token 繼續嘗試");
        return;
      }

      if (!tokenResult.success || !tokenResult.token) {
        logger.error("WebSocket", "重連時無法取得有效的 token，取消重連並導回登入頁");
        setConnected(false);
        await client.deactivate();
        apiService.showReauthRequiredDialog(() => apiService.clearAuthAndRedirectToLogin());
        return false;
      }

      // 更新 connectHeaders 使用新的 token
      if (tokenResult.token !== latestToken) {
        logger.info("WebSocket", "重連前 token 已刷新，更新連線 headers");
        client.connectHeaders = {
          ...client.connectHeaders,
          Authorization: `Bearer ${tokenResult.token}`,
        };
      } else {
        // 即使 token 沒變，也確保使用最新的 headers
        client.connectHeaders = {
          ...client.connectHeaders,
          Authorization: `Bearer ${tokenResult.token}`,
        };
      }

      // 如果有原始的 beforeConnect，執行它
      if (originalBeforeConnect) {
        const result = await originalBeforeConnect();
        return result !== false; // 確保返回 boolean
      }
      
      return true; // 允許重連
    };

    client.activate(); // 啟動連線
    clientRef.current = client;
  }, [endpoint]);

  useEffect(() => {
    // 若未提供 endpoint，略過建立連線
    if (!endpoint) {
      setConnected(false);
      return;
    }

    // 執行連線（包含 token 驗證）
    connectWithTokenValidation();

    // unmount 或 endpoint 改變時關閉；
    return () => {
	  if (clientRef.current) {
        logger.info("WebSocket", "Deactivate STOMP client");
        console.log("🔚 Deactivate STOMP client");
        clientRef.current.deactivate(); // 會自動處理 unsubscribe & close
      }
    };
  }, [endpoint, connectWithTokenValidation]);

  return {
    client: clientRef.current,
    connected,
  };
}
